import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import { Box, Button, GlobalStyles, Skeleton, Snackbar, Alert, Stack } from '@mui/material';
import { CartProvider } from './contexts/CartContext';
import Header from './components/Header';
import MobileNav from './components/MobileNav';
import NotificationListener from './components/NotificationListener';
import { getCurrentUser } from './services/authService';
import { I18nProvider } from './i18n/I18nProvider';

const Home = lazy(() => import('./components/Home'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const Restaurants = lazy(() => import('./components/Restaurants'));
const RestaurantDetail = lazy(() => import('./components/RestaurantDetail'));
const Cart = lazy(() => import('./components/Cart'));
const Orders = lazy(() => import('./components/Orders'));
const OrderTracking = lazy(() => import('./components/OrderTracking'));
const ParcelDeliveries = lazy(() => import('./components/ParcelDeliveries'));
const ParcelTracking = lazy(() => import('./components/ParcelTracking'));
const Profile = lazy(() => import('./components/Profile'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const RestaurantDashboard = lazy(() => import('./components/RestaurantDashboard'));
const DriverDashboard = lazy(() => import('./components/DriverDashboard'));
const RestaurantReports = lazy(() => import('./components/RestaurantReports'));
const PartnerRegistration = lazy(() => import('./components/PartnerRegistration'));
const ShopifyOrderTracking = lazy(() => import('./components/ShopifyOrderTracking'));

const createAppTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: '#0f766e',
      dark: '#0b5b56',
      light: '#34a59a',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#1f2937',
      dark: '#111827',
      light: '#374151',
      contrastText: '#ffffff',
    },
    background: {
      default: mode === 'dark' ? '#0f1115' : '#f6f7f9',
      paper: mode === 'dark' ? '#151922' : '#ffffff',
    },
    text: {
      primary: mode === 'dark' ? '#f8fafc' : '#0f172a',
      secondary: mode === 'dark' ? '#cbd5f5' : '#475569',
    },
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily: '"Atkinson Hyperlegible", "Segoe UI", "Arial", sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    body1: { fontSize: '1.05rem', lineHeight: 1.7 },
    body2: { fontSize: '0.98rem', lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 700, fontSize: '1rem' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          paddingLeft: 20,
          paddingRight: 20,
          minHeight: 48, // Accessibility: minimum touch target
          fontWeight: 700,
          '&:focus-visible': {
            outline: `3px solid ${mode === 'dark' ? '#7dd3fc' : '#0f766e'}`,
            outlineOffset: 2,
          },
        },
        containedPrimary: {
          boxShadow: '0 10px 20px rgba(15, 118, 110, 0.2)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: mode === 'dark' ? '#151922' : '#ffffff',
          borderRadius: 18,
          border: mode === 'dark'
            ? '1px solid rgba(148, 163, 184, 0.2)'
            : '1px solid rgba(15, 23, 42, 0.08)',
          boxShadow: mode === 'dark'
            ? '0 20px 50px rgba(0, 0, 0, 0.35)'
            : '0 20px 50px rgba(15, 23, 42, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundImage: 'none',
          backgroundColor: mode === 'dark' ? '#151922' : '#ffffff',
          border: mode === 'dark'
            ? '1px solid rgba(148, 163, 184, 0.18)'
            : '1px solid rgba(15, 23, 42, 0.06)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 700,
          minHeight: 32,
          '&:focus-visible': {
            outline: `2px solid ${mode === 'dark' ? '#7dd3fc' : '#0f766e'}`,
            outlineOffset: 1,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
          '&:focus-visible': {
            outline: `3px solid ${mode === 'dark' ? '#7dd3fc' : '#0f766e'}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            color: mode === 'dark' ? '#cbd5e1' : '#64748b',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#0f766e',
          },
          '& .MuiOutlinedInput-root': {
            color: mode === 'dark' ? '#f8fafc' : '#0f172a',
            backgroundColor: mode === 'dark' ? alpha('#0f172a', 0.9) : '#f8fafc',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? 'rgba(148, 163, 184, 0.28)' : 'rgba(148, 163, 184, 0.32)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? '#94a3b8' : '#64748b',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0f766e',
              borderWidth: 2,
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: mode === 'dark' ? '#94a3b8' : '#64748b',
              opacity: 1,
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#94a3b8' : '#64748b',
        },
      },
    },
  },
});

function App() {
  const [themeMode, setThemeMode] = useState('light');
  const [serviceWorkerWaiting, setServiceWorkerWaiting] = useState(null);

  useEffect(() => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) {
      setThemeMode(savedMode);
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setThemeMode(prefersDark ? 'dark' : 'light');
    }
  }, []);

  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  useEffect(() => {
    const handleServiceWorkerUpdate = (event) => {
      setServiceWorkerWaiting(event.detail?.waiting || null);
    };
    window.addEventListener('sw-update-ready', handleServiceWorkerUpdate);
    return () => window.removeEventListener('sw-update-ready', handleServiceWorkerUpdate);
  }, []);

  useEffect(() => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const { origin } = new URL(apiUrl);
      if (origin !== window.location.origin) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = origin;
        document.head.appendChild(link);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const handleBadge = (event) => {
      const count = Number(event.detail?.count) || 0;
      if ('setAppBadge' in navigator) {
        if (count > 0) {
          navigator.setAppBadge(count).catch(() => {});
        } else {
          navigator.clearAppBadge().catch(() => {});
        }
      }
    };
    window.addEventListener('app-badge-update', handleBadge);
    return () => window.removeEventListener('app-badge-update', handleBadge);
  }, []);

  const refreshApp = () => {
    if (serviceWorkerWaiting) {
      serviceWorkerWaiting.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  };

  const theme = createAppTheme(themeMode);

  const RoleRoute = ({ roles, element }) => {
    const user = getCurrentUser();
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (roles && !roles.includes(user.role)) {
      if (user.role === 'admin') return <Navigate to="/admin" replace />;
      if (user.role === 'restaurant') return <Navigate to="/restaurant" replace />;
      if (user.role === 'restaurant_supervisor') return <Navigate to="/restaurant/reports" replace />;
      if (user.role === 'driver') return <Navigate to="/driver" replace />;
      return <Navigate to="/orders" replace />;
    }
    return element;
  };

  const AuthRoute = ({ element }) => {
    const user = getCurrentUser();
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    return element;
  };

  const CustomerBrowseRoute = ({ element }) => {
    const user = getCurrentUser();
    if (!user || user.role === 'customer') {
      return element;
    }
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'restaurant') return <Navigate to="/restaurant" replace />;
    if (user.role === 'restaurant_supervisor') return <Navigate to="/restaurant/reports" replace />;
    if (user.role === 'driver') return <Navigate to="/driver" replace />;
    return <Navigate to="/" replace />;
  };

  const AppShell = () => {
    const location = useLocation();
    const pathname = location.pathname;
    const isControlRoom = ['/admin', '/restaurant', '/driver'].some((prefix) => pathname.startsWith(prefix));
    const isImmersive = ['/profile', '/orders', '/cart'].some((prefix) => pathname.startsWith(prefix));

    return (
      <Box
        component="main"
        sx={{
          width: '100%',
          minHeight: 'calc(100vh - 76px)',
          pb: { xs: 12, md: 5 },
          pt: { xs: 2, md: 3 },
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            width: '100%',
            maxWidth: isControlRoom ? '1680px' : isImmersive ? '1480px' : '1360px',
            px: { xs: 1.5, sm: 2.5, md: 3, xl: 4 },
            mx: 'auto',
          }}
        >
          <Suspense
            fallback={(
              <Box sx={{ py: 2 }}>
                <Stack spacing={2}>
                  <Skeleton variant="rounded" height={180} />
                  <Skeleton variant="rounded" height={60} />
                  <Stack direction="row" spacing={2}>
                    <Skeleton variant="rounded" height={280} sx={{ flex: 1 }} />
                    <Skeleton variant="rounded" height={280} sx={{ flex: 1 }} />
                    <Skeleton variant="rounded" height={280} sx={{ flex: 1 }} />
                  </Stack>
                </Stack>
              </Box>
            )}
          >
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/register" element={<Register />} />
              <Route path="/partner/register" element={<PartnerRegistration />} />
              <Route path="/track/shopify/:orderId" element={<ShopifyOrderTracking />} />
              <Route path="/restaurants" element={<CustomerBrowseRoute element={<Restaurants />} />} />
              <Route path="/restaurants/:id" element={<CustomerBrowseRoute element={<RestaurantDetail />} />} />
              <Route path="/cart" element={<CustomerBrowseRoute element={<Cart />} />} />
              <Route path="/orders" element={<RoleRoute roles={['customer']} element={<Orders />} />} />
              <Route path="/orders/:id/track" element={<RoleRoute roles={['customer']} element={<OrderTracking />} />} />
              <Route path="/parcels" element={<RoleRoute roles={['customer']} element={<ParcelDeliveries />} />} />
              <Route path="/parcels/:id/track" element={<RoleRoute roles={['customer']} element={<ParcelTracking />} />} />
              <Route path="/profile" element={<AuthRoute element={<Profile />} />} />
              <Route path="/admin" element={<RoleRoute roles={['admin']} element={<AdminDashboard />} />} />
              <Route path="/restaurant" element={<RoleRoute roles={['restaurant']} element={<RestaurantDashboard />} />} />
              <Route path="/restaurant/reports" element={<RoleRoute roles={['restaurant', 'restaurant_supervisor']} element={<RestaurantReports />} />} />
              <Route path="/driver" element={<RoleRoute roles={['driver']} element={<DriverDashboard />} />} />
            </Routes>
          </Suspense>
        </Container>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          body: {
            background:
              themeMode === 'dark'
                ? 'radial-gradient(circle at top, rgba(125, 211, 252, 0.08), transparent 55%), linear-gradient(180deg, rgba(15, 17, 21, 1) 0%, rgba(20, 24, 33, 1) 100%)'
                : 'radial-gradient(circle at top, rgba(15, 118, 110, 0.08), transparent 55%), linear-gradient(180deg, rgba(246, 247, 249, 1) 0%, rgba(255, 255, 255, 1) 70%)',
            minHeight: '100vh',
          },
          '::selection': {
            background: 'rgba(15, 118, 110, 0.2)',
          },
          '*:focus-visible': {
            outline: `3px solid ${themeMode === 'dark' ? '#7dd3fc' : '#0f766e'}`,
            outlineOffset: 2,
          },
        }}
      />
      <I18nProvider>
        <CartProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Header themeMode={themeMode} toggleTheme={toggleTheme} />
            <NotificationListener />
            <AppShell />
            <MobileNav />
            <Snackbar
              open={Boolean(serviceWorkerWaiting)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              sx={{ bottom: { xs: 108, md: 24 } }}
            >
              <Alert
                severity="info"
                action={
                  <Button color="inherit" size="small" onClick={refreshApp}>
                    Refresh
                  </Button>
                }
                onClose={() => setServiceWorkerWaiting(null)}
                sx={{ width: '100%' }}
              >
                A newer version of the app is ready.
              </Alert>
            </Snackbar>
          </Router>
        </CartProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
